import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Calendar, Plus, Clock, User, Building2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Badge } from '@/components/common/Badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/common/Dialog';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { useToast } from '@/components/common/Toast';
import SearchableSelect from '@/components/common/SearchableSelect';
import CallViewHeader from './CallViewHeader';
import CallStatsCards from './CallStatsCards';
import { CALL_TYPE_CONFIG, STATUS_CONFIG } from '@/theme/corporateTheme';

// Types matching Prisma schema
interface CallTask {
  id: string;
  contactId: string;
  callType: 'CALL1' | 'CALL2' | 'CALL3';
  dueDate: string;
  assignedToId: string;
  status: 'OPEN' | 'DONE' | 'SKIPPED';
  callPackData: {
    type?: string;
    duration?: number;
    notes?: string;
  } | null;
  contact: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    currentStage: string | null;
    account: {
      id: string;
      name: string;
      tier: string;
    };
  };
  assignedTo: {
    id: string;
    name: string;
    email: string;
  };
}

interface Account {
  id: string;
  name: string;
  tier: string;
}

interface Contact {
  id: string;
  accountId: string;
  firstName: string;
  lastName: string;
  email: string;
  currentStage: string | null;
}

const ABM_STAGES: Record<string, string> = {
  'STAGE_01': 'Awareness',
  'STAGE_02': 'Interest',
  'STAGE_03': 'Consideration',
  'STAGE_04': 'Intent',
  'STAGE_05': 'Evaluation',
  'STAGE_06': 'Purchase',
};

export default function CallManagementPage() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  
  const [calls, setCalls] = useState<CallTask[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  
  // Form state
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedContactId, setSelectedContactId] = useState('');
  const [accountLabel, setAccountLabel] = useState('');
  const [contactLabel, setContactLabel] = useState('');
  const [callType, setCallType] = useState<'CALL1' | 'CALL2' | 'CALL3'>('CALL1');
  const [dueDate, setDueDate] = useState('');
  const [duration, setDuration] = useState(30);
  const [notes, setNotes] = useState('');

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  // Contacts are searched on demand per account via the SearchableSelect below,
  // so no global contact pre-fetch/filter is needed here.


  const loadData = async () => {
    try {
      setLoading(true);

      // Load accounts and contacts from API
      const [accountsRes, contactsRes] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/accounts?limit=5000`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } }),
        fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/contacts?limit=1000`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } }),
      ]);

      if (accountsRes.ok) {
        const data = await accountsRes.json();
        setAccounts(data.data || []);
      }

      if (contactsRes.ok) {
        const data = await contactsRes.json();
        setContacts(data.data || []);
      }

      // Load calls from API
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        // Backend returns { data: [...], total, page, totalPages }
        setCalls(result.data || result);
      } else {
        console.error('Failed to load calls:', response.status, response.statusText);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to load call data',
      });
      setLoading(false);
    }
  };

  const handleCreateCall = async () => {
    try {
      // Get user ID from JWT token
      const token = localStorage.getItem('accessToken');
      let assignedToId = 'user-test-001'; // fallback
      
      if (token) {
        try {
          // Decode JWT (it's base64 encoded, middle part contains payload)
          const payload = JSON.parse(atob(token.split('.')[1]));
          assignedToId = payload.userId || 'user-test-001';
        } catch (e) {
          console.warn('Failed to decode token, using fallback user ID');
        }
      }

      // Validate
      if (!selectedContactId || !dueDate) {
        addToast({
          type: 'error',
          title: 'Validation Error',
          description: 'Please select contact and due date',
        });
        return;
      }

      // Create call task
      const response = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/call-tasks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
        body: JSON.stringify({
          contactId: selectedContactId,
          callType: callType,
          dueDate: new Date(dueDate).toISOString(),
          assignedToId: assignedToId,
          status: 'OPEN',
          callPackData: {
            type: CALL_TYPE_CONFIG[callType].label,
            duration: duration,
            notes: notes,
          },
        }),
      });

      if (response.ok) {
        addToast({
          type: 'success',
          title: 'Success',
          description: 'Call scheduled successfully',
        });
        setShowCreateDialog(false);
        resetForm();
        loadData();
      } else {
        throw new Error('Failed to create call');
      }
    } catch (error) {
      console.error('Error creating call:', error);
      addToast({
        type: 'error',
        title: 'Error',
        description: 'Failed to schedule call',
      });
    }
  };

  const resetForm = () => {
    setSelectedAccountId('');
    setSelectedContactId('');
    setCallType('CALL1');
    setDueDate('');
    setDuration(30);
    setNotes('');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  // Calculate stats
  const stats = {
    total: calls.length,
    scheduled: calls.filter(c => c.status === 'OPEN').length,
    completed: calls.filter(c => c.status === 'DONE').length,
    overdue: calls.filter(c => {
      if (c.status !== 'OPEN') return false;
      return new Date(c.dueDate) < new Date();
    }).length,
    dueToday: calls.filter(c => {
      const due = new Date(c.dueDate);
      const today = new Date();
      return due.toDateString() === today.toDateString();
    }).length,
  };

  return (
    <div className="space-y-6 p-6">
      {/* Unified Header */}
      <CallViewHeader
        currentView="cards"
        title="Call Management"
        subtitle="Manage your ABM outreach calls"
        onCreateClick={() => setShowCreateDialog(true)}
        showCreateButton={true}
      />

      {/* Unified Stats */}
      <CallStatsCards stats={stats} />

      {/* Calls List */}
      <Card>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">Scheduled Calls</h2>
          
          {calls.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Phone className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No calls scheduled yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowCreateDialog(true)}
              >
                Schedule Your First Call
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {calls.map((call) => (
                <Card 
                  key={call.id} 
                  className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/calls/${call.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4 flex-1">
                      {/* Call Type Badge */}
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-2xl ${
                        call.callType === 'CALL1' ? 'bg-blue-100' :
                        call.callType === 'CALL2' ? 'bg-indigo-100' : 'bg-emerald-100'
                      }`}>
                        {CALL_TYPE_CONFIG[call.callType].icon}
                      </div>

                      {/* Call Info */}
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h3 className="font-semibold text-gray-900">
                            {call.contact?.firstName || 'Unknown'} {call.contact?.lastName || 'Contact'}
                          </h3>
                          <Badge className={CALL_TYPE_CONFIG[call.callType].color}>
                            {CALL_TYPE_CONFIG[call.callType].label}
                          </Badge>
                          <Badge className={STATUS_CONFIG[call.status].color}>
                            {STATUS_CONFIG[call.status].label}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                          <div className="flex items-center">
                            <Building2 className="w-4 h-4 mr-1" />
                            {call.contact?.account?.name || 'Unknown Account'}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-1" />
                            {formatDate(call.dueDate)}
                          </div>
                          {call.contact?.currentStage && (
                            <div className="flex items-center">
                              <TrendingUp className="w-4 h-4 mr-1" />
                              {ABM_STAGES[call.contact.currentStage] || call.contact.currentStage}
                            </div>
                          )}
                        </div>

                        {call.callPackData?.notes && (
                          <p className="text-sm text-muted-foreground mt-2">
                            {call.callPackData.notes}
                          </p>
                        )}
                      </div>

                      {/* Assigned User */}
                      <div className="text-right">
                        <div className="flex items-center text-sm text-muted-foreground">
                          <User className="w-4 h-4 mr-1" />
                          {call.assignedTo.name}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* Create Call Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule New Call</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Account Selection */}
            <div>
              <Label htmlFor="account">Account *</Label>
              <SearchableSelect
                value={selectedAccountId}
                valueLabel={accountLabel}
                placeholder="Search account…"
                onChange={(id, opt) => { setSelectedAccountId(id); setAccountLabel(opt?.label || ''); setSelectedContactId(''); setContactLabel(''); }}
                fetchOptions={async (q) => {
                  const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/accounts?limit=25${q ? `&search=${encodeURIComponent(q)}` : ''}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } });
                  const data = res.ok ? await res.json() : { data: [] };
                  return (data.data || []).map((a: any) => ({ id: a.id, label: a.name, sublabel: [a.industryVertical, a.region].filter(Boolean).join(' · ') }));
                }}
              />
            </div>

            {/* Contact Selection */}
            <div>
              <Label htmlFor="contact">Contact *</Label>
              <SearchableSelect
                value={selectedContactId}
                valueLabel={contactLabel}
                placeholder="Search contact…"
                disabled={!selectedAccountId}
                onChange={(id, opt) => { setSelectedContactId(id); setContactLabel(opt?.label || ''); }}
                fetchOptions={async (q) => {
                  if (!selectedAccountId) return [];
                  const res = await fetch(`${import.meta.env.VITE_API_URL ?? ''}/api/contacts?account_id=${selectedAccountId}&limit=50${q ? `&search=${encodeURIComponent(q)}` : ''}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } });
                  const data = res.ok ? await res.json() : { data: [] };
                  return (data.data || []).map((c: any) => ({ id: c.id, label: `${c.firstName} ${c.lastName}`.trim(), sublabel: c.title || c.email }));
                }}
              />
              {!selectedAccountId && (
                <p className="text-sm text-muted-foreground mt-1">
                  Please select an account first
                </p>
              )}
            </div>

            {/* Call Type */}
            <div>
              <Label htmlFor="callType">Call Type *</Label>
              <select
                id="callType"
                value={callType}
                onChange={(e) => setCallType(e.target.value as 'CALL1' | 'CALL2' | 'CALL3')}
                className="w-full px-3 py-2 border border-input rounded-md bg-background"
              >
                <option value="CALL1">Discovery Call</option>
                <option value="CALL2">Demo Call</option>
                <option value="CALL3">Follow-up Call</option>
              </select>
            </div>

            {/* Due Date */}
            <div>
              <Label htmlFor="dueDate">Due Date & Time *</Label>
              <Input
                id="dueDate"
                type="datetime-local"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                required
              />
            </div>

            {/* Duration */}
            <div>
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                type="number"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                min={15}
                max={120}
              />
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-md bg-background min-h-[80px]"
                placeholder="Add call notes or agenda..."
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateDialog(false);
                resetForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateCall}>
              Schedule Call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
